/**
 * Pie / donut chart builder (ADR-0079 §4.1, §4.4).
 *
 * One slice per row. `chartType: "pie"` defaults to `innerRadiusRatio = 0`
 * (full pie); `chartType: "donut"` defaults to `innerRadiusRatio = 0.5`
 * (donut hole). Spec can override either.
 *
 * The renderer feeds rows into `series[0].data` as `{ name, value }` objects
 * because ECharts pie series doesn't support the cartesian `dataset.encode`
 * pattern — rows are read here at render time and reshaped into the named
 * objects pie expects.
 */

import type { PieSpec } from "../echartsSpec.js";
import {
  buildNoDataGraphic,
  buildTooltipFormatter,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type EChartsOption = Record<string, unknown>;

const DEFAULT_INNER_PIE = 0;
const DEFAULT_INNER_DONUT = 0.5;

export function buildPieOption(
  spec: PieSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const categoryIdx = resolveColumnIndex(spec.encoding.category.column, dimensions);
  const valueIdx = resolveColumnIndex(spec.encoding.value.column, dimensions);

  const data: Array<{ name: string; value: number | null }> =
    categoryIdx === null || valueIdx === null
      ? []
      : rows.map((row) => ({
          name: row[categoryIdx] == null ? "" : String(row[categoryIdx]),
          value: coerce(row[valueIdx]),
        }));

  const innerDefault =
    spec.chartType === "donut" ? DEFAULT_INNER_DONUT : DEFAULT_INNER_PIE;
  const innerRatio = clamp01(spec.options.innerRadiusRatio ?? innerDefault);

  const labelPosition = spec.options.labelPosition ?? "outside";
  const showLabel = labelPosition !== "none";

  const palette = resolvePalette(spec.options.color, data.length);
  const tooltipFormatter = buildTooltipFormatter(
    spec.options.tooltip,
    [{ name: spec.encoding.value.column }],
    spec.encoding.value.format,
  );

  const pieSeries: Record<string, unknown> = {
    type: "pie",
    name: spec.encoding.value.column,
    radius: [`${Math.round(innerRatio * 100)}%`, "75%"],
    data,
    label: showLabel
      ? { show: true, position: labelPosition }
      : { show: false },
  };

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    ...(palette ? { color: palette } : {}),
    legend: buildLegend(spec.options.legend),
    tooltip: {
      show: spec.options.tooltip?.show !== false,
      trigger: "item",
      ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
    },
    series: [pieSeries],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(0.95, n));
}

function buildLegend(
  legend: PieSpec["options"]["legend"],
): Record<string, unknown> {
  const show = legend?.show !== false;
  const base: Record<string, unknown> = { show };
  switch (legend?.position) {
    case "top":
      base.top = 0;
      base.left = "center";
      break;
    case "bottom":
      base.bottom = 0;
      base.left = "center";
      break;
    case "left":
      base.left = 0;
      base.top = "middle";
      base.orient = "vertical";
      break;
    case "right":
      base.right = 0;
      base.top = "middle";
      base.orient = "vertical";
      break;
  }
  return base;
}
